import { defaultPrefix } from 'all-in-one-shared-api';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Title from 'antd/lib/typography/Title';
import { Button, Card, Checkbox, Col, Form, Input, Row, Space, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { MigrationFlowContext } from '../../contexts/migration/context';
import { MigrationFlowData } from '../../contexts/migration/MigrationFlowData';
import { ActionType } from '../../contexts/actionType';
import generateKey from '../../utils/generateKey';
import Rocket from 'assets/images/first-login.svg';
import { mobileRegex } from '../../utils/regex';
import styled from 'styled-components';
import { HomeContext } from 'contexts/home/context';
import { UserAccessActionTypes } from '../../contexts/home/actions';
import { MobileNumberPrefix } from 'components';
import { useKeycloakReady } from 'hooks';
import { CommonTnc } from 'components/CommonTnc';

const StyledImage = styled.img`
  height: 6.25rem;
`;

const StyledCard = styled(Card)`
  border-radius: 8;
  padding: 16;
  margin-top: 16;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-explain-error {
    display: flex;
    justify-content: center;
  }
`;

export const MigrationFirstTimeLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tokenParsed } = useKeycloakReady();
  const location = useLocation();

  const { state, dispatch } = useContext(MigrationFlowContext);
  const { state: homeState, dispatch: homeDispatch } = useContext(HomeContext);

  const loginId = tokenParsed?.preferred_username;

  const [form] = Form.useForm();

  const onFinish = (values: MigrationFlowData) => {
    dispatch({ type: ActionType.SubmitForm, payload: values });

    const { mobile, countryCode } = values ?? {};

    if (values.mobile && values.mobile.length > 0) {
      navigate('/register/otp-verification', {
        state: {
          loginId: loginId,
          mobile: mobile ?? '',
          countryCode: countryCode ?? '',
          prevPath: location.pathname,
        },
      });
    } else {
      homeDispatch({
        type: UserAccessActionTypes.REFRESH,
        payload: { key: generateKey() },
      });
      navigate('/home');
    }
  };

  const handleChange = (value: string) => {
    form.setFieldsValue({
      countryCode: value,
    });
    dispatch({ type: ActionType.SubmitForm, payload: { ...state, countryCode: value } });
  };

  const prefixSelector = (
    <Form.Item name="countryCode" noStyle>
      <MobileNumberPrefix handleChange={handleChange} />
    </Form.Item>
  );

  return (
    <Form
      layout={'vertical'}
      form={form}
      onFinish={onFinish}
      initialValues={{
        ['countryCode']: defaultPrefix,
      }}
    >
      <Space align="center" direction="vertical" style={{ display: 'flex' }} size="middle">
        <Space align="center" direction="vertical">
          <StyledImage src={Rocket} />
          <Title level={3}>{t('migration.firstLogin.title')}</Title>
        </Space>
        <StyledCard>
          <Row justify="center" align="middle">
            <Col>
              <div style={{ paddingBottom: 32 }}>
                <Typography.Text>{t('migration.firstLogin.description')}</Typography.Text>
              </div>
              {(!homeState?.userAccess?.mobile || homeState?.userAccess?.mobile?.length <= 0) && (
                <Form.Item
                  name="mobile"
                  label={
                    <Row>
                      <Typography.Text>{t('migration.firstLogin.mobile')}</Typography.Text>
                      <Typography.Text>{t('migration.firstLogin.optional')}</Typography.Text>
                    </Row>
                  }
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (!value || mobileRegex.test(value)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('registration.error.invalidMobile')));
                      },
                    }),
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <div style={{ paddingBottom: 32 }}>
                    <Input addonBefore={prefixSelector} style={{ width: '100%' }} />
                  </div>
                </Form.Item>
              )}
              <StyledFormItem
                name="tncAgree"
                valuePropName="checked"
                rules={[
                  () => ({
                    validator(_, value) {
                      return value ? Promise.resolve() : Promise.reject(new Error(t('registration.error.required')));
                    },
                  }),
                ]}
                style={{ marginBottom: 12 }}
              >
                <Row justify="center" align="middle">
                  <Space direction="horizontal">
                    <Checkbox name="remember">
                      <CommonTnc />
                    </Checkbox>
                  </Space>
                </Row>
              </StyledFormItem>
            </Col>
          </Row>
        </StyledCard>
        <Form.Item>
          <Space direction="horizontal">
            <Button block type="primary" size="large" htmlType="submit" style={{ marginTop: 16 }}>
              {t('common.confirm')}
            </Button>
          </Space>
        </Form.Item>
      </Space>
    </Form>
  );
};
